import { useState } from 'react';
import { Bug, Lightbulb, HelpCircle, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useFeedback, FeedbackType, BugSeverity } from '@/hooks/useFeedback';
import { cn } from '@/lib/utils';

interface FeedbackDialogProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const feedbackTypes = [
  { value: 'feature' as FeedbackType, label: 'Feature Request', icon: Lightbulb, emoji: '💡' },
  { value: 'bug' as FeedbackType, label: 'Bug Report', icon: Bug, emoji: '🐛' },
  { value: 'question' as FeedbackType, label: 'Question', icon: HelpCircle, emoji: '❓' },
];

const severityOptions = [
  { value: 'blocking' as BugSeverity, label: "Blocking - I can't continue using the app", emoji: '🔴' },
  { value: 'annoying' as BugSeverity, label: 'Annoying - I can work around it', emoji: '🟡' },
  { value: 'minor' as BugSeverity, label: 'Minor - Just noticed it', emoji: '🟢' },
];

export function FeedbackDialog({ trigger, open: controlledOpen, onOpenChange }: FeedbackDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (v: boolean) => {
    setInternalOpen(v);
    onOpenChange?.(v);
  };
  const [type, setType] = useState<FeedbackType>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity | undefined>(undefined);
  const [showSuccess, setShowSuccess] = useState(false);
  const { submitFeedback, isSubmitting } = useFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const success = await submitFeedback(
      type, 
      title.trim(), 
      description.trim(),
      type === 'bug' ? severity : undefined
    );
    
    if (success) {
      setShowSuccess(true);
      setTimeout(() => {
        setOpen(false);
      }, 2000);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setType('feature');
    setSeverity(undefined);
    setShowSuccess(false);
  };

  const getPlaceholders = () => {
    switch (type) {
      case 'feature':
        return {
          title: 'e.g., Add dark mode toggle',
          description: 'Describe your feature idea in detail...',
          titleLabel: 'What feature would you like?',
          descLabel: 'Tell us more (optional)',
        };
      case 'bug':
        return {
          title: "e.g., Button doesn't respond on mobile",
          description: '1. Go to...\n2. Click on...\n3. See error...',
          titleLabel: "What's the issue?",
          descLabel: 'Steps to reproduce (optional)',
        };
      case 'question':
        return {
          title: 'e.g., How do I export my data?',
          description: 'Add any additional context...',
          titleLabel: 'What would you like to know?',
          descLabel: 'Additional context (optional)',
        };
    }
  };

  const placeholders = getPlaceholders();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      // Reset form when dialog opens or closes to ensure fresh state
      resetForm();
    }}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      {!trigger && controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Lightbulb className="h-4 w-4" />
            Feedback
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-lg font-semibold">Thanks for your feedback!</h3>
              <p className="text-sm text-muted-foreground">We review everything personally.</p>
            </div>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Submit Feedback</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Feedback Type Selector */}
              <div className="grid grid-cols-3 gap-2">
                {feedbackTypes.map((ft) => (
                  <button
                    key={ft.value}
                    type="button"
                    onClick={() => {
                      setType(ft.value);
                      if (ft.value !== 'bug') setSeverity(undefined);
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all',
                      'hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                      type === ft.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground'
                    )}
                  >
                    <span className="text-lg">{ft.emoji}</span>
                    <span className="text-xs font-medium text-center leading-tight">
                      {ft.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Title Field */}
              <div className="space-y-2">
                <Label htmlFor="feedback-title">{placeholders.titleLabel}</Label>
                <Input
                  id="feedback-title"
                  placeholder={placeholders.title}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              {/* Description Field */}
              <div className="space-y-2">
                <Label htmlFor="feedback-desc">{placeholders.descLabel}</Label>
                <Textarea
                  id="feedback-desc"
                  placeholder={placeholders.description}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Severity Field (Bug Reports Only) */}
              {type === 'bug' && (
                <div className="space-y-3">
                  <Label>How severe is this?</Label>
                  <RadioGroup
                    value={severity || ''}
                    onValueChange={(val) => setSeverity(val as BugSeverity)}
                    className="space-y-2"
                  >
                    {severityOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={cn(
                          'flex items-center space-x-3 rounded-lg border p-3 cursor-pointer transition-colors',
                          severity === opt.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        )}
                        onClick={() => setSeverity(opt.value)}
                      >
                        <RadioGroupItem value={opt.value} id={`severity-${opt.value}`} />
                        <Label
                          htmlFor={`severity-${opt.value}`}
                          className="flex-1 cursor-pointer font-normal"
                        >
                          <span className="mr-2">{opt.emoji}</span>
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || !title.trim()}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
